import React, { useState, useCallback, useEffect } from 'react';
import './App.css';

// Contexts
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Shared Components
import { Header, Footer, UnderlinedText, LegalDocumentModal, PasswordGate } from './components/shared';

// Section Components
import {
  HeroSection,
  FeatureSection,
  LocationSection,
  BetaTestSection,
  SocialMediaSection
} from './components/sections';

// Page Components
import { BetaTestPage } from './components/pages';

// Background images
import heroBg from './assets/boy_sitting_in_the_campus.png';
import universeBg from './assets/girl_talking_big_image.png';

/**
 * App Content Component - Uses language context for translations
 */
function AppContent() {
  const { t } = useLanguage();

  // Determine initial page from URL
  const getPageFromUrl = () => {
    const path = window.location.pathname;
    if (path === '/beta-test' || path === '/beta-test/') {
      return 'beta-test';
    }
    return 'home';
  };

  // State for page navigation
  const [currentPage, setCurrentPage] = useState(getPageFromUrl);

  // State for legal document modal
  const [openLegalDocument, setOpenLegalDocument] = useState(null);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromUrl());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Navigation callbacks
  const navigateToBetaTest = useCallback((scrollToRegistration = false) => {
    const url = scrollToRegistration ? '/beta-test#registration-form' : '/beta-test';
    window.history.pushState({ page: 'beta-test' }, '', url);
    setCurrentPage('beta-test');

    // Scroll to top - BetaTestPage component handles hash-based scrolling
    window.scrollTo(0, 0);
  }, []);

  const navigateToHome = useCallback(() => {
    window.history.pushState({ page: 'home' }, '', '/');
    setCurrentPage('home');
    window.scrollTo(0, 0);
  }, []);

  // Callbacks for opening legal documents
  const handleOpenLegalDocument = useCallback((documentType) => {
    setOpenLegalDocument(documentType);
  }, []);

  const handleCloseLegalDocument = useCallback(() => {
    setOpenLegalDocument(null);
  }, []);

  // Feature data for the three feature sections - uses translations
  const features = [
    {
      id: 'community',
      title: <><UnderlinedText color="yellow">{t('community.title.highlight')}</UnderlinedText>{t('community.title.rest')}</>,
      description: t('community.description'),
      subFeatures: [
        {
          title: t('community.subFeature1.title'),
          description: t('community.subFeature1.description')
        },
        {
          title: t('community.subFeature2.title'),
          description: t('community.subFeature2.description')
        }
      ],
      phoneImage: '/screens/screen_post.png',
      phoneAlt: t('a11y.feedScreenAlt'),
      reversed: false
    },
    {
      id: 'marketplace',
      title: <>{t('marketplace.title.start')}<UnderlinedText color="yellow">{t('marketplace.title.highlight')}</UnderlinedText></>,
      description: t('marketplace.description'),
      subFeatures: [
        {
          title: t('marketplace.subFeature1.title'),
          description: t('marketplace.subFeature1.description')
        }
      ],
      phoneImage: '/screens/screen_marketplace_wg.png',
      phoneAlt: t('a11y.marketplaceScreenAlt'),
      reversed: true
    },
    {
      id: 'reviews',
      title: <>{t('reviews.title.start')}<UnderlinedText color="yellow">{t('reviews.title.highlight')}</UnderlinedText>{t('reviews.title.end')}</>,
      description: t('reviews.description'),
      subFeatures: [
        {
          title: t('reviews.subFeature1.title'),
          description: t('reviews.subFeature1.description')
        }
      ],
      phoneImage: '/screens/screen_review.png',
      phoneAlt: t('a11y.reviewsScreenAlt'),
      reversed: false
    }
  ];

  // Render Beta Test Page
  if (currentPage === 'beta-test') {
    return <BetaTestPage onNavigateHome={navigateToHome} />;
  }

  // Render Landing Page (default)
  return (
    <div className="app">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Header + Hero wrapper with shared astronaut background */}
      <div
        className="hero-wrapper"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        {/* Dark overlay for better text readability */}
        <div className="hero-wrapper-overlay" aria-hidden="true"></div>

        {/* Header (Issue #423: beta CTA navigates to top of beta-test page) */}
        <Header onBetaClick={() => navigateToBetaTest(false)} />

        {/* Hero Section */}
        <HeroSection onNavigateToBetaTest={navigateToBetaTest} />
      </div>

      {/* Main Content */}
      <main id="main-content" role="main">
        {/* Feature Sections */}
        {features.map((feature) => (
          <FeatureSection
            key={feature.id}
            id={feature.id}
            title={feature.title}
            description={feature.description}
            subFeatures={feature.subFeatures}
            phoneImage={feature.phoneImage}
            phoneAlt={feature.phoneAlt}
            reversed={feature.reversed}
          />
        ))}

        {/* Location Section */}
        <LocationSection onNavigateToBetaTest={navigateToBetaTest} />

        {/* Beta Test CTA Section - solid dark background, matches LocationSection */}
        <BetaTestSection onNavigateToBetaTest={navigateToBetaTest} />
      </main>

      {/* Bottom sections wrapper with shared universe background (Social + Footer only) */}
      <div
        className="bottom-wrapper"
        style={{ backgroundImage: `url(${universeBg})` }}
      >
        {/* Dark overlay for better text readability */}
        <div className="bottom-wrapper-overlay" aria-hidden="true"></div>

        {/* Social Media Section */}
        <SocialMediaSection />

        {/* Footer */}
        <Footer onOpenLegalDocument={handleOpenLegalDocument} />
      </div>

      {/* Legal Document Modal */}
      <LegalDocumentModal
        documentType={openLegalDocument}
        isOpen={openLegalDocument !== null}
        onClose={handleCloseLegalDocument}
      />
    </div>
  );
}

/**
 * Main App Component - Landing Page for universe-app.online
 * Wraps content with LanguageProvider for i18n support
 *
 * Structure:
 * 1. Header - Logo and navigation
 * 2. Hero Section - Main banner with headline and CTA
 * 3. Feature Section 1 - Community features
 * 4. Feature Section 2 - Marketplace features
 * 5. Feature Section 3 - Course reviews features
 * 6. Location Section - Northern Germany beta launch
 * 7. Beta Test CTA Section - solid dark background
 * 8. Social Media Section - shares universe background image with Footer
 * 9. Footer - Links and copyright
 */
function App() {
  return (
    <PasswordGate>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </PasswordGate>
  );
}

export default App;
